import { useEffect, useRef } from 'react';

import { makeStyles, Paper } from '@material-ui/core';
import { Alert } from '@mui/material';
import qs from 'qs';
import { useQuery } from 'react-query';
import {
  Link,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { usePlanLimit } from 'src/context/PlanLimitContext';
import { getYearPeriodsBySubscription } from 'src/utils/subscription';

import AnalyticalRanking from './AnalyticalRanking';
import SubmitRanking from './SubmitRanking';
import SummuryRanking from './SummaryRanking';
import MetaTags from '../../components/Seo/MetaTags';
import MarketsNavigation from '../../components/UI/MarketsNavigation/MarketsNavigation';
import SubHeader from '../../components/UI/SubHeader/SubHeader';
import { useAuthContext } from '../../context/AuthContextProvider';
import { getInformativeTextByLocation } from '../../services/informative';
import {
  findAllFilters,
  findFilters,
  getFirstYearFromFilters,
  getRankingHasConsolidated,
  getRankingMethodology,
} from '../../services/ranking';
import { useQueryParams } from '../../utils/hooks';
import { ScrollToTop } from '../../utils/scroll';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3, 4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2),
    },
  },
  container: {
    '@media print': {
      zoom: '80%',
    },
  },
}));

const INITIAL_STATE = {
  market: 'CRI',
  ranking: null,
  criteria: null,
  year: null,
  timeRange: null,
};

export default function Rankings() {
  const classes = useStyles();
  const { auth } = useAuthContext();
  const history = useHistory();
  const { path } = useRouteMatch();
  const [{ query }, setQuery] = useQueryParams(INITIAL_STATE);
  const { showBanner } = usePlanLimit();
  const showAnalyticalRanking =
    query.ranking && query.criteria && query.year && query.timeRange;

  const rankingsRef = useRef();
  const { pathname, search } = useLocation();

  const handlePrint = useReactToPrint({
    content: () => rankingsRef.current,
  });

  const { data, isLoading } = useQuery(
    'filters',
    () => findFilters(undefined, auth.isLoggedIn),
    {
      onSuccess: (data) => {
        if (!query.year) {
          setQuery({
            ...query,
            year: getFirstYearFromFilters(data, query.market),
          });
        }
      },
    },
  );

  const { data: allFilters, isLoading: isLoadingAllFilters } = useQuery(
    'allFilters',
    () => findAllFilters(undefined, auth.isLoggedIn),
  );

  const { data: infoData } = useQuery('info', () =>
    getInformativeTextByLocation('rank'),
  );

  const markets = data && Object.keys(data);

  const hasConsolidated =
    data && getRankingHasConsolidated(data, query.market, query.ranking);

  const methodology =
    data && getRankingMethodology(data, query.market, query.ranking);

  const isCRWithoutRankings =
    data && query.market === 'CR' && !data?.CR?.rankings?.length;

  const marketLabels = isCRWithoutRankings
    ? {
        CR: 'CR',
      }
    : undefined;

  const disabledMarkets = isCRWithoutRankings ? ['CR'] : [];

  const handleChangeMarket = (value) => {
    const search = qs.stringify({
      market: value,
      year: data?.[value]?.years?.[0],
    });
    history.push({ pathname: '/rankings', search });
  };

  const currentPlanHasPeriodLimitation = () => {
    if (auth?.user?.subscription?.plan?.type === 'vip') {
      return false;
    }

    const yearPeriods = getYearPeriodsBySubscription(auth?.user?.subscription);

    return (
      auth.isLoggedIn && query.year && !yearPeriods.includes(Number(query.year))
    );
  };

  const hasMissingQueryParams = () => {
    return !query.ranking || !query.criteria || !query.year || !query.timeRange;
  };

  useEffect(() => {
    if (showAnalyticalRanking) {
      rankingsRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [query]);

  useEffect(() => {
    showBanner();
  }, []);

  return (
    <ScrollToTop>
      <div id="rankings-page" ref={rankingsRef} className={classes.container}>
        <MetaTags
          title="Rankings Uqbar"
          description="Produzidos desde 2004 e baseados em critérios definidos sob medida para cada um dos mercados, os Rankings Uqbar classificam operações e prestadores de serviços. O conjunto completo de todos os Rankings Uqbar está disponível em nossa plataforma."
        />
        <SubHeader
          title="Rankings"
          subTitle={infoData?.data?.rank?.informationText}
          breadcrumbs={[
            {
              title: 'Rankings',
              link: '/rankings',
            },
          ]}
        />
        <Paper square elevation={0} className={classes.paper}>
          <Switch>
            <Route exact path={path}>
              <MarketsNavigation
                value={query.market}
                handleSelectMarket={handleChangeMarket}
                filters={data}
                marketLabels={marketLabels}
                disabledMarkets={disabledMarkets}
              />

              <SummuryRanking
                state={query}
                setState={setQuery}
                rankings={data && data[query.market]?.rankings}
                filters={data}
                shouldValidateCR={query.market?.toLowerCase() === 'cr'}
              />
            </Route>
            <Route exact path={`${path}/dados`}>
              {currentPlanHasPeriodLimitation() && (
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                  Os dados para o ano selecionado estão limitados no seu plano
                  atual.&nbsp;
                  <Link
                    to="/planos"
                    style={{
                      textDecoration: 'underline',
                      color: 'inherit',
                    }}>
                    Faça upgrade
                  </Link>
                  &nbsp;para ver mais dados.
                </Alert>
              )}
              {!auth.isLoggedIn && (
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                  Os dados de exibição estão limitados.&nbsp;
                  <Link
                    to={`/login?redirectUrl=${pathname}${search}`}
                    style={{
                      textDecoration: 'underline',
                      color: 'inherit',
                    }}>
                    Faça login
                  </Link>
                  &nbsp;para ver mais dados.
                </Alert>
              )}
              {hasMissingQueryParams() && (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                  Para exibir os dados, é necessário selecionar os
                  parâmetros&nbsp;
                  <b>Ranking</b>, <b>Critério</b>, <b>Período</b> e <b>Visão</b>
                  .
                </Alert>
              )}
              <MarketsNavigation
                value={query.market}
                handleSelectMarket={handleChangeMarket}
                filters={data}
                marketLabels={marketLabels}
                disabledMarkets={disabledMarkets}
              />
              <AnalyticalRanking
                filters={data}
                allFilters={allFilters}
                state={query}
                setState={setQuery}
                hasConsolidated={hasConsolidated}
                markets={markets}
                rankings={data && data[query.market]?.rankings}
                methodology={methodology}
                handlePrint={handlePrint}
                loadingFilters={isLoading || isLoadingAllFilters}
              />
            </Route>
            <Route exact path={`${path}/como-participar`}>
              <SubmitRanking />
            </Route>
          </Switch>
        </Paper>
      </div>
    </ScrollToTop>
  );
}
